// Abrechnung: Mehr-Produkt pro Kunde. Kunde aktiviert Produkte selbst per
// Stripe-Checkout; Admin weist Produkte zu.

import { useEffect, useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useSearchParams } from 'react-router-dom';
import toast from 'react-hot-toast';
import { CreditCard, Download, ExternalLink, Loader2, RefreshCw, Plus, Trash2, CheckCircle2, AlertTriangle, XCircle } from 'lucide-react';
import { useAuthStore } from '@/hooks/useAuth';
import { getCustomers } from '@/lib/api';
import {
  getBilling, activateProduct, cancelProduct, getCheckoutStatus, openPortal,
  getCatalog, assignProduct, removeProduct, createCatalogItem,
  formatMoney, intervalLabels, productStatusInfo,
  type BillingInterval, type CustomerProduct,
} from '@/lib/billing-api';

function formatDate(v: string | number | null | undefined): string {
  if (!v) return '—';
  return new Date(v).toLocaleDateString('de-DE', { day: '2-digit', month: '2-digit', year: 'numeric' });
}

/** `fixedCustomerId`: eingebettet im Kunden-Arbeitsbereich — Kundenwahl entfällt. */
export default function AbrechnungPage({ fixedCustomerId }: { fixedCustomerId?: string } = {}) {
  const { user } = useAuthStore();
  const isAdmin = user?.role === 'admin';
  const queryClient = useQueryClient();
  const [searchParams, setSearchParams] = useSearchParams();

  const [selectedCustomerId, setSelectedCustomerId] = useState<string | null>(fixedCustomerId || null);
  const customerId = isAdmin ? selectedCustomerId : user?.customer_id || null;
  const headerCustomerId = isAdmin ? selectedCustomerId || undefined : undefined;

  const { data: customers } = useQuery({ queryKey: ['customers'], queryFn: getCustomers, enabled: isAdmin });
  useEffect(() => {
    if (isAdmin && !selectedCustomerId && customers && customers.length > 0) setSelectedCustomerId(customers[0].id);
  }, [isAdmin, selectedCustomerId, customers]);

  const { data, isLoading, error, refetch, isFetching } = useQuery({
    queryKey: ['billing', customerId],
    queryFn: () => getBilling(headerCustomerId),
    enabled: !!customerId,
    staleTime: 30_000,
  });

  // Nach Checkout: Status pollen bis aktiv
  useEffect(() => {
    const checkout = searchParams.get('checkout');
    const sessionId = searchParams.get('session_id');
    if (checkout === 'canceled') { toast('Aktivierung abgebrochen.', { icon: 'ℹ️' }); setSearchParams({}, { replace: true }); return; }
    if (checkout !== 'success' || !sessionId || !customerId) return;
    let cancelled = false, attempts = 0;
    const poll = async () => {
      attempts++;
      try {
        const s = await getCheckoutStatus(sessionId, headerCustomerId);
        if (cancelled) return;
        if (s.ready) { toast.success('Produkt aktiviert – vielen Dank!'); queryClient.invalidateQueries({ queryKey: ['billing'] }); setSearchParams({}, { replace: true }); return; }
      } catch { /* weiter */ }
      if (!cancelled && attempts < 6) setTimeout(poll, 2000);
      else if (!cancelled) { setSearchParams({}, { replace: true }); queryClient.invalidateQueries({ queryKey: ['billing'] }); }
    };
    poll();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams, customerId]);

  const [busyId, setBusyId] = useState<string | null>(null);
  const activate = async (cp: CustomerProduct) => {
    setBusyId(cp.id);
    try { window.location.href = await activateProduct(cp.id, headerCustomerId); }
    catch (err: any) { toast.error(err.message || 'Aktivierung fehlgeschlagen'); setBusyId(null); }
  };
  const [portalLoading, setPortalLoading] = useState(false);
  const portal = async () => {
    setPortalLoading(true);
    try { window.location.href = await openPortal(headerCustomerId); }
    catch (err: any) { toast.error(err.message || 'Portal konnte nicht geöffnet werden'); setPortalLoading(false); }
  };
  const cancelMut = useMutation({
    mutationFn: ({ id, action }: { id: string; action: 'cancel' | 'reactivate' }) => cancelProduct(id, action, headerCustomerId),
    onSuccess: () => { queryClient.invalidateQueries({ queryKey: ['billing'] }); toast.success('Aktualisiert.'); },
    onError: (e: any) => toast.error(e.message || 'Aktion fehlgeschlagen'),
  });

  const products = data?.products || [];
  const invoices = data?.invoices || [];

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className={fixedCustomerId ? 'hidden' : ''}>
          <h1 className="text-2xl font-bold text-gray-900">Abrechnung</h1>
          <p className="text-sm text-gray-500 mt-1">
            {isAdmin ? 'Produkte pro Kunde verwalten und Abrechnungen einsehen.' : 'Deine Produkte, Rechnungen und Zahlungsmethode.'}
          </p>
        </div>
        <div className="flex items-center gap-2">
          {isAdmin && customers && !fixedCustomerId && (
            <select value={selectedCustomerId || ''} onChange={(e) => setSelectedCustomerId(e.target.value)}
              className="px-3 py-2 border border-gray-300 rounded-lg text-sm bg-white focus:ring-2 focus:ring-primary-500 max-w-[220px]">
              {customers.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
            </select>
          )}
          <button onClick={() => refetch()} disabled={isFetching} className="p-2 border border-gray-300 rounded-lg text-gray-600 hover:bg-gray-50" title="Aktualisieren">
            {isFetching ? <Loader2 className="w-4 h-4 animate-spin" /> : <RefreshCw className="w-4 h-4" />}
          </button>
        </div>
      </div>

      {!customerId ? (
        <div className="bg-white rounded-lg border border-gray-200 p-8 text-center text-gray-500">
          {isAdmin ? 'Kunden werden geladen…' : 'Deinem Konto ist kein Kunde zugeordnet.'}
        </div>
      ) : isLoading ? (
        <div className="bg-white rounded-lg border border-gray-200 p-8 flex justify-center"><Loader2 className="w-6 h-6 animate-spin text-gray-400" /></div>
      ) : error ? (
        <div className="bg-white rounded-lg border border-red-200 p-6 text-sm text-red-600">Abrechnung konnte nicht geladen werden: {(error as any).message}</div>
      ) : (
        <>
          {/* Produkte */}
          <div className="space-y-3">
            <h2 className="text-sm font-semibold text-gray-900">Produkte</h2>
            {products.length === 0 ? (
              <div className="bg-white rounded-lg border border-gray-200 p-6 text-center text-sm text-gray-500">
                {isAdmin ? 'Noch keine Produkte. Weise unten ein Produkt zu.' : 'Aktuell sind keine Produkte hinterlegt.'}
              </div>
            ) : products.map((cp) => {
              const badge = productStatusInfo[cp.status];
              return (
                <div key={cp.id} className="bg-white rounded-lg border border-gray-200 p-4">
                  <div className="flex flex-wrap items-center justify-between gap-3">
                    <div>
                      <div className="flex items-center gap-2">
                        <p className="font-semibold text-gray-900">{cp.name}</p>
                        <span className={`px-2 py-0.5 rounded-full text-[11px] font-medium ${badge.cls}`}>{badge.label}</span>
                      </div>
                      <p className="text-sm text-gray-500 mt-0.5">
                        {formatMoney(cp.amount, cp.currency)} {intervalLabels[cp.interval]}
                        {cp.status === 'active' && cp.interval !== 'one_time' && cp.current_period_end && (
                          <span className="ml-2">· {cp.cancel_at_period_end ? `endet am ${formatDate(cp.current_period_end)}` : `verlängert sich am ${formatDate(cp.current_period_end)}`}</span>
                        )}
                      </p>
                    </div>
                    <div className="flex items-center gap-2">
                      {cp.status === 'pending' && (
                        <button onClick={() => activate(cp)} disabled={busyId === cp.id}
                          className="inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-primary-600 hover:bg-primary-700 text-white text-sm font-medium disabled:opacity-60">
                          {busyId === cp.id ? <Loader2 className="w-4 h-4 animate-spin" /> : <CreditCard className="w-4 h-4" />} Jetzt aktivieren
                        </button>
                      )}
                      {cp.status === 'past_due' && (
                        <button onClick={() => activate(cp)} disabled={busyId === cp.id}
                          className="inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-amber-600 hover:bg-amber-700 text-white text-sm font-medium disabled:opacity-60">
                          {busyId === cp.id ? <Loader2 className="w-4 h-4 animate-spin" /> : <AlertTriangle className="w-4 h-4" />} Zahlung nachholen
                        </button>
                      )}
                      {cp.status === 'active' && cp.interval !== 'one_time' && (
                        cp.cancel_at_period_end ? (
                          <button onClick={() => cancelMut.mutate({ id: cp.id, action: 'reactivate' })} disabled={cancelMut.isPending}
                            className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg border border-amber-200 text-amber-700 text-xs font-medium hover:bg-amber-50">
                            <RefreshCw className="w-3.5 h-3.5" /> Kündigung zurücknehmen
                          </button>
                        ) : (
                          <button onClick={() => { if (window.confirm(`„${cp.name}" kündigen? Läuft bis Periodenende weiter.`)) cancelMut.mutate({ id: cp.id, action: 'cancel' }); }} disabled={cancelMut.isPending}
                            className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg border border-red-200 text-red-600 text-xs font-medium hover:bg-red-50">
                            <XCircle className="w-3.5 h-3.5" /> Kündigen
                          </button>
                        )
                      )}
                      {isAdmin && (
                        <button onClick={() => { if (window.confirm(`„${cp.name}" für diesen Kunden entfernen?`)) removeProduct(cp.id).then(() => queryClient.invalidateQueries({ queryKey: ['billing'] })).catch((e) => toast.error(e.message)); }}
                          className="p-2 rounded-lg text-gray-400 hover:text-red-600 hover:bg-red-50" title="Produkt entfernen (Admin)">
                          <Trash2 className="w-4 h-4" />
                        </button>
                      )}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>

          {/* PUBLIVO-Abo (wird in PUBLIVO verwaltet) */}
          {data?.publivo && (
            <div className="bg-white rounded-lg border border-primary-200 p-5 flex flex-wrap items-center justify-between gap-3">
              <div>
                <h2 className="text-sm font-semibold text-gray-900">PUBLIVO-Blog-Abo</h2>
                <p className="text-xs text-gray-500 mt-0.5">Dein PUBLIVO-Abo wird direkt in PUBLIVO verwaltet (Rechnungen &amp; Zahlung).</p>
              </div>
              <a
                href={data.publivo.manage_url}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-2 px-3 py-2 rounded-lg border border-primary-200 text-primary-700 text-sm font-medium hover:bg-primary-50"
              >
                <ExternalLink className="w-4 h-4" /> PUBLIVO-Abo verwalten
              </a>
            </div>
          )}

          {/* Admin: Produkt zuweisen */}
          {isAdmin && <AssignPanel customerId={customerId} onDone={() => queryClient.invalidateQueries({ queryKey: ['billing'] })} />}

          {/* Rechnungen */}
          <div className="bg-white rounded-lg border border-gray-200 overflow-hidden">
            <div className="px-5 py-3 border-b border-gray-100"><h2 className="text-sm font-semibold text-gray-900">Rechnungen</h2></div>
            {invoices.length === 0 ? (
              <div className="px-5 py-8 text-center text-sm text-gray-500">Noch keine Rechnungen vorhanden.</div>
            ) : (
              <div className="overflow-x-auto">
                <table className="min-w-full text-sm">
                  <thead className="bg-gray-50"><tr className="text-left text-xs text-gray-400 uppercase tracking-wider">
                    <th className="px-5 py-2 font-medium">Datum</th><th className="px-5 py-2 font-medium">Nr.</th>
                    <th className="px-5 py-2 font-medium text-right">Betrag</th><th className="px-5 py-2 font-medium">Status</th><th className="px-5 py-2 font-medium text-right">PDF</th>
                  </tr></thead>
                  <tbody className="divide-y divide-gray-50">
                    {invoices.map((inv) => (
                      <tr key={inv.id}>
                        <td className="px-5 py-3 whitespace-nowrap">{formatDate(inv.created)}</td>
                        <td className="px-5 py-3 whitespace-nowrap font-mono text-xs">{inv.number || inv.id.slice(0, 10)}</td>
                        <td className="px-5 py-3 text-right whitespace-nowrap font-medium">{formatMoney(inv.total, inv.currency)}</td>
                        <td className="px-5 py-3 whitespace-nowrap">
                          <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[11px] font-medium ${inv.status === 'paid' ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-600'}`}>
                            {inv.status === 'paid' && <CheckCircle2 className="w-3 h-3" />}{inv.status === 'paid' ? 'Bezahlt' : (inv.status || '—')}
                          </span>
                        </td>
                        <td className="px-5 py-3 text-right whitespace-nowrap">
                          {inv.invoice_pdf ? <a href={inv.invoice_pdf} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1 text-primary-600 hover:underline text-xs font-medium"><Download className="w-3.5 h-3.5" /> PDF</a>
                            : inv.hosted_invoice_url ? <a href={inv.hosted_invoice_url} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1 text-primary-600 hover:underline text-xs font-medium"><ExternalLink className="w-3.5 h-3.5" /> Ansehen</a>
                              : <span className="text-xs text-gray-400">—</span>}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>

          {/* Zahlungsmethode */}
          {data?.has_stripe_customer && (
            <div className="bg-white rounded-lg border border-gray-200 p-5">
              <h2 className="text-sm font-semibold text-gray-900 mb-2">Zahlungsmethode &amp; Adresse</h2>
              <p className="text-xs text-gray-500 mb-4">Zahlungsmethode oder Rechnungsadresse ändern – sicher über das Stripe-Kundenportal.</p>
              <button onClick={portal} disabled={portalLoading} className="inline-flex items-center gap-2 px-3 py-2 rounded-lg border border-gray-300 text-sm text-gray-700 hover:bg-gray-50 disabled:opacity-60">
                {portalLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : <ExternalLink className="w-4 h-4" />} Zum Kundenportal
              </button>
            </div>
          )}
        </>
      )}
    </div>
  );
}

// ── Admin: Produkt zuweisen ─────────────────────────────────────────────────
function AssignPanel({ customerId, onDone }: { customerId: string; onDone: () => void }) {
  const { data: catalog } = useQuery({ queryKey: ['billing-catalog'], queryFn: getCatalog });
  const queryClient = useQueryClient();
  const [catalogId, setCatalogId] = useState('');
  const [name, setName] = useState('');
  const [euro, setEuro] = useState('');
  const [interval, setInterval] = useState<BillingInterval>('month');
  const [saveTemplate, setSaveTemplate] = useState(false);

  const onPick = (id: string) => {
    setCatalogId(id);
    const item = catalog?.find((c) => c.id === id);
    if (item) { setName(item.name); if (item.default_amount != null) setEuro((item.default_amount / 100).toString()); setInterval(item.interval); }
  };

  const assign = useMutation({
    mutationFn: async () => {
      const amount = Math.round(parseFloat(euro.replace(',', '.')) * 100);
      if (!name.trim() || !Number.isFinite(amount) || amount <= 0) throw new Error('Name und gültiger Preis erforderlich');
      if (saveTemplate && !catalogId) await createCatalogItem({ name: name.trim(), default_amount: amount, interval });
      return assignProduct({ customer_id: customerId, catalog_id: catalogId || null, name: name.trim(), amount, interval });
    },
    onSuccess: () => { toast.success('Produkt zugewiesen – erscheint beim Kunden als „noch nicht aktiviert".'); setName(''); setEuro(''); setCatalogId(''); setSaveTemplate(false); queryClient.invalidateQueries({ queryKey: ['billing-catalog'] }); onDone(); },
    onError: (e: any) => toast.error(e.message || 'Zuweisung fehlgeschlagen'),
  });

  return (
    <div className="bg-white rounded-lg border border-primary-200 p-5">
      <div className="flex items-center gap-2 mb-3"><Plus className="w-4 h-4 text-primary-600" /><h2 className="text-sm font-semibold text-gray-900">Produkt zuweisen (Admin)</h2></div>
      <div className="grid md:grid-cols-4 gap-3">
        <div className="md:col-span-1">
          <label className="block text-xs text-gray-500 mb-1">Vorlage (optional)</label>
          <select value={catalogId} onChange={(e) => onPick(e.target.value)} className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm bg-white">
            <option value="">– frei –</option>
            {(catalog || []).map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
          </select>
        </div>
        <div className="md:col-span-1">
          <label className="block text-xs text-gray-500 mb-1">Produktname</label>
          <input value={name} onChange={(e) => setName(e.target.value)} placeholder="z.B. Google-Ads-Betreuung" className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm" />
        </div>
        <div>
          <label className="block text-xs text-gray-500 mb-1">Preis (€ netto)</label>
          <input value={euro} onChange={(e) => setEuro(e.target.value)} inputMode="decimal" placeholder="z.B. 500" className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm" />
        </div>
        <div>
          <label className="block text-xs text-gray-500 mb-1">Abrechnung</label>
          <select value={interval} onChange={(e) => setInterval(e.target.value as BillingInterval)} className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm bg-white">
            <option value="month">monatlich</option><option value="year">jährlich</option><option value="one_time">einmalig</option>
          </select>
        </div>
      </div>
      <div className="flex items-center justify-between mt-3">
        <label className="inline-flex items-center gap-2 text-xs text-gray-500">
          <input type="checkbox" checked={saveTemplate} onChange={(e) => setSaveTemplate(e.target.checked)} disabled={!!catalogId} className="rounded border-gray-300" />
          Als Vorlage im Katalog speichern
        </label>
        <button onClick={() => assign.mutate()} disabled={assign.isPending} className="inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-primary-600 hover:bg-primary-700 text-white text-sm font-medium disabled:opacity-60">
          {assign.isPending ? <Loader2 className="w-4 h-4 animate-spin" /> : <Plus className="w-4 h-4" />} Produkt zuweisen
        </button>
      </div>
      <p className="text-[11px] text-gray-400 mt-2">Legt ein eigenes Stripe-Produkt für diesen Kunden an. Der Kunde aktiviert es selbst im Dashboard.</p>
    </div>
  );
}
